import { GameClient, podOriginName } from "../net";
import { logout, fetchMe, enterGameSession, backToGm } from "../api";
import { esc } from "./util";
import { openPodScheduleOverlay } from "./podSchedule";

// Slide-in game menu (☰). Consolidates everything that used to clutter the top
// bar: identity/role, stations-left, game schedule, calculator mode, game info,
// GM-view toggle, and back-to-dashboard. The map keeps only the timer + locate.
// Shared by the seeker and hider views; mounts its button into #game-menu-btn.

export function mountGameMenu(root: HTMLElement, client: GameClient, opts: {
  onExit?: () => void;
  stat?: () => string | null;       // e.g. seeker's stations remaining
  showInfo?: boolean;               // show the Game-info item (default true; off for GM console)
}) {
  const btn = root.querySelector<HTMLElement>("#game-menu-btn");
  if (!btn) return;
  root.insertAdjacentHTML("beforeend", `<div class="nav-scrim" id="nav-scrim" hidden></div><aside class="nav-panel" id="nav-panel" aria-hidden="true"></aside>`);
  const scrim = root.querySelector<HTMLElement>("#nav-scrim")!;
  const panel = root.querySelector<HTMLElement>("#nav-panel")!;
  let open = false;
  let photo = "";
  let teamExpanded = false;
  const memberName = (sub: string) => client.team?.members.find((m) => m.sub === sub)?.name ?? client.pod?.names?.[sub] ?? sub.slice(0, 5);
  function teamSection(): string {
    const t = client.team; if (!t || client.identity.gm) return "";
    const lead = client.isLeader;
    const leaderIds = t.leaderIds ?? [];
    const leaders = leaderIds.map(memberName).join(" & ") || "none yet";
    const others = (t.members ?? []).filter((m) => !leaderIds.includes(m.sub));
    return `<div class="nav-team">
      <button class="nav-item" id="nav-team-toggle"><span class="nav-ic">👥</span>Team &amp; leadership${t.name ? ` · ${esc(t.name)}` : ""}<span class="nav-caret">${teamExpanded ? "▾" : "▸"}</span></button>
      ${teamExpanded ? `<div class="nav-teambox">
        <div class="nav-teamrow"><span class="nav-teamcap">Team name</span>${lead ? `<input class="nav-teamname" id="nav-tname" maxlength="40" value="${esc(t.name ?? "")}" placeholder="Name your team"/>` : `<b>${esc(t.name ?? "—")}</b>`}</div>
        <div class="nav-teamrow"><span class="nav-teamcap">Leader${leaderIds.length > 1 ? "s" : ""}</span><b>${esc(leaders)}</b></div>
        ${lead
          ? `${others.length ? `<div class="nav-teamcap" style="margin-top:9px">Share or transfer leadership</div>${others.map((m) => `<div class="nav-leadrow"><span>${esc(m.name)}</span><span class="nav-leadbtns">${leaderIds.length < 2 ? `<button class="ghost" data-coleader="${m.sub}">+ Co-leader</button>` : ""}<button class="ghost" data-transfer="${m.sub}">Transfer ▸</button></span></div>`).join("")}` : `<div class="sk-note">No online teammates to pass to.</div>`}
             <button class="ghost nav-stepdown" id="nav-stepdown">Step down as leader</button>`
          : leaderIds.length < 2 ? `<button class="primary" id="nav-claim" style="width:100%;margin-top:8px">Claim leadership</button>` : `<div class="sk-note">This team has two leaders.</div>`}
      </div>` : ""}
    </div>`;
  }
  function wireTeam() {
    panel.querySelector("#nav-team-toggle")?.addEventListener("click", () => { teamExpanded = !teamExpanded; render(); });
    const tn = panel.querySelector<HTMLInputElement>("#nav-tname");
    tn?.addEventListener("change", () => client.setTeamName(tn.value.trim()));
    panel.querySelectorAll<HTMLButtonElement>("[data-coleader]").forEach((b) => b.addEventListener("click", () => { if (confirm(`Make ${memberName(b.dataset.coleader!)} a co-leader? You'll share leadership of the team (max 2).`)) client.makeCoLeader(b.dataset.coleader!); }));
    panel.querySelectorAll<HTMLButtonElement>("[data-transfer]").forEach((b) => b.addEventListener("click", () => { if (confirm(`Transfer your leadership to ${memberName(b.dataset.transfer!)}? You'll no longer be a leader of this team.`)) client.transferLeader(b.dataset.transfer!); }));
    panel.querySelector("#nav-stepdown")?.addEventListener("click", () => { if (confirm("Step down as team leader?")) client.releaseLeader(); });
    panel.querySelector("#nav-claim")?.addEventListener("click", () => client.claimLeader());
  }

  const setOpen = (o: boolean) => { open = o; panel.classList.toggle("on", o); scrim.hidden = !o; panel.setAttribute("aria-hidden", String(!o)); if (o) render(); };
  btn.addEventListener("click", () => setOpen(!open));
  scrim.addEventListener("click", () => setOpen(false));

  // GM impersonation: a persistent "viewing as" bar that exits straight to GM.
  const exitToGm = () => backToGm(client.gameId).then(() => location.reload()).catch((e) => alert((e as Error).message));
  if (client.identity.imp) {
    document.body.classList.add("imp-active");
    root.insertAdjacentHTML("beforeend", `<button class="imp-bar" id="imp-bar">👁 GM view of <b>${esc(client.identity.name || "player")}</b> · tap to exit</button>`);
    root.querySelector("#imp-bar")!.addEventListener("click", exitToGm);
  }

  fetchMe().then((me) => { if (me?.photo) { photo = me.photo; if (open) render(); } }).catch(() => {});

  function podContext(): string {
    const p = client.pod; if (!p?.schedule) return "";
    const total = p.schedule.pods.length;
    const done = p.roundIdx >= total;
    const who = client.identity.gm ? "" : ` · you're <b>${client.role === "hider" ? "HIDING" : "SEEKING"}</b>`;
    const head = done ? `Game complete · ${total} rounds` : `Round <b>${p.roundIdx + 1}</b> of ${total}${who}`;
    return `<div class="nav-podctx">${head}${!done ? ` · started near <b>${esc(podOriginName(p.origin))}</b>` : ""}</div>`;
  }

  function render() {
    const role = (client.identity.gm ? "GAMEMASTER" : (client.role ?? client.identity.role ?? "seeker")).toUpperCase();
    const stat = opts.stat?.();
    panel.innerHTML = `
      <div class="nav-head">
        <span class="nav-dot ${photo ? "has-photo" : ""}" data-role="${role.toLowerCase()}" style="${photo ? `background-image:url('${photo}')` : ""}"></span>
        <div class="nav-id"><div class="nav-name">${esc(client.identity.name || "Player")}</div><div class="nav-role">${role}</div></div>
        <button class="nav-x" id="nav-close" aria-label="Close">×</button>
      </div>
      ${podContext()}
      ${stat ? `<div class="nav-stat"><span class="nav-stat-n">${esc(stat)}</span><span class="nav-stat-cap">stations remaining</span></div>` : ""}
      ${teamSection()}
      <nav class="nav-items">
        ${client.pod ? `<button class="nav-item" id="nav-sched"><span class="nav-ic">📋</span>Game schedule</button>` : ""}
        <button class="nav-item" id="nav-calc"><span class="nav-ic">🧮</span>Calculator mode</button>
        ${opts.showInfo === false ? "" : `<button class="nav-item" id="nav-info"><span class="nav-ic">ℹ️</span>Game info</button>`}
        ${client.identity.alsoGm && !client.identity.imp ? `<button class="nav-item" id="nav-gm"><span class="nav-ic">🛠</span>Switch to GM view</button>` : ""}
        <button class="nav-item nav-bottom ${client.identity.imp || opts.onExit ? "" : "danger"}" id="nav-exit"><span class="nav-ic">←</span>${client.identity.imp ? "Back to GM console" : opts.onExit ? "Back to dashboard" : "Log out"}</button>
      </nav>
      <div class="nav-foot">JET LAG NYC</div>`;
    panel.querySelector("#nav-close")!.addEventListener("click", () => setOpen(false));
    panel.querySelector("#nav-sched")?.addEventListener("click", () => { setOpen(false); openPodScheduleOverlay(client); });
    panel.querySelector("#nav-calc")!.addEventListener("click", () => { const u = new URL(location.href); u.searchParams.set("calc", "1"); location.href = u.toString(); });
    panel.querySelector("#nav-info")?.addEventListener("click", () => { setOpen(false); document.getElementById("info-fab")?.click(); });
    panel.querySelector("#nav-gm")?.addEventListener("click", async () => { try { await enterGameSession(client.gameId, "gm"); location.reload(); } catch (e) { alert((e as Error).message); } });
    panel.querySelector("#nav-exit")!.addEventListener("click", () => { if (client.identity.imp) exitToGm(); else opts.onExit ? opts.onExit() : logout(); });
    wireTeam();
  }
  client.subscribe(() => { if (open && !panel.querySelector("#nav-tname:focus")) render(); });

  client.subscribe(() => { if (open) render(); });
}
